import { useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { CheckCircle, XCircle, Info, X } from 'lucide-react';
import { useUIStore } from '@/stores/ui-store';

export default function Toast() {
  const { toast, hideToast } = useUIStore();

  // Auto-dismiss after delay
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(hideToast, 4000);
      return () => clearTimeout(timer);
    }
  }, [toast, hideToast]);

  const config = {
    success: {
      icon: CheckCircle,
      iconColor: 'text-green-400',
      bgColor: 'bg-green-500/10',
      borderColor: 'border-green-500/20',
      glowColor: 'shadow-[0_0_20px_rgba(34,197,94,0.15)]',
    },
    error: {
      icon: XCircle,
      iconColor: 'text-red-400',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/20',
      glowColor: 'shadow-[0_0_20px_rgba(239,68,68,0.15)]',
    },
    info: {
      icon: Info,
      iconColor: 'text-accent-blue',
      bgColor: 'bg-accent-blue/10',
      borderColor: 'border-accent-blue/20',
      glowColor: 'shadow-[0_0_20px_rgba(0,212,255,0.15)]',
    },
  };

  return (
    <AnimatePresence>
      {toast && (
        <motion.div
          initial={{ opacity: 0, y: -20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{ type: 'spring', damping: 25, stiffness: 400 }}
          className="fixed top-4 left-4 right-4 z-[200] safe-area-top"
        >
          <div
            className={`
              ${config[toast.type].bgColor}
              ${config[toast.type].borderColor}
              ${config[toast.type].glowColor}
              backdrop-blur-xl border rounded-2xl p-4 flex items-center gap-3
            `}
          >
            {/* Icon with pulse effect for success */}
            <div className="relative flex-shrink-0">
              {(() => {
                const Icon = config[toast.type].icon;
                return (
                  <Icon
                    size={22}
                    className={`${config[toast.type].iconColor} relative z-10`}
                  />
                );
              })()}
              {toast.type === 'success' && (
                <motion.div
                  initial={{ scale: 1, opacity: 0.5 }}
                  animate={{ scale: 2, opacity: 0 }}
                  transition={{ duration: 0.6 }}
                  className="absolute inset-0 rounded-full bg-green-400"
                />
              )}
            </div>

            {/* Message */}
            <span className="flex-1 text-sm font-medium text-white/90">{toast.message}</span>

            {/* Close button */}
            <button
              onClick={hideToast}
              className="flex-shrink-0 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
            >
              <X size={16} className="text-white/50" />
            </button>

            {/* Progress bar */}
            <motion.div
              initial={{ scaleX: 1 }}
              animate={{ scaleX: 0 }}
              transition={{ duration: 4, ease: 'linear' }}
              className={`absolute bottom-0 left-0 right-0 h-0.5 origin-left rounded-full ${
                toast.type === 'success'
                  ? 'bg-green-400/50'
                  : toast.type === 'error'
                  ? 'bg-red-400/50'
                  : 'bg-accent-blue/50'
              }`}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
